/**
 * academyReadinessEngine.js — contrat de readiness du tenant Academy (référence).
 *
 * Recadrage demandé par Alfred 2026-07-13 : l'ancien `validateAcademyTenantReadiness()`
 * mélangeait trois catégories de preuves de nature différente sous des booléens
 * plats jamais renseignés nulle part (`evidence.routes.academyAccessible`,
 * `evidence.supervision.masterCanInspect/preservesTenantContext`) — le step ne
 * pouvait donc JAMAIS passer au vert, quel que soit l'état réel du tenant.
 *
 * Ce module sépare explicitement :
 *  1. PROVISIONNEMENT — dérivable automatiquement des données persistées
 *     (tenant actif, modules, utilisateurs de test, campus, école).
 *  2. ACCESSIBILITÉ — preuve d'un contrôle runtime réel (RPC get_tenant_public_identity
 *     + requête scopée), déclenché explicitement par un Master et journalisé
 *     dans `audit_logs` (action ACADEMY_ACCESSIBILITY_CHECK).
 *  3. SUPERVISION — preuve produite par le mécanisme d'inspection RÉEL déjà
 *     existant (`TenantContext.startTenantInspection`/`stopTenantInspection`),
 *     qui journalise déjà chaque bascule dans `audit_logs` (action
 *     MASTER_CONTEXT_SWITCH) — enrichi ici de vérifications réelles au lieu
 *     d'un simple log de navigation.
 *
 * Aucune preuve n'est un booléen sans provenance : chaque preuve porte au
 * minimum `status`, `source`, le tenant concerné et `checkedAt`.
 */
import { supabase } from './supabase';

export const ACADEMY_SLUGS = ['rectorium-academy', 'academy'];

const asArray = (value) => (Array.isArray(value) ? value : []);

/** 1. Provisionnement — dérivé automatiquement, aucun contrôle runtime nécessaire. */
export function deriveAcademyProvisioning(tenant, { profiles, campuses, schools, tenantCapabilities } = {}) {
  if (!tenant?.id) {
    return {
      tenantActive: false, modulesEnabled: false,
      testUsersPresent: false, campusPresent: false, schoolPresent: false,
    };
  }
  const academyId = tenant.id;
  const activatedModules = tenant.activated_modules || tenant.enabled_modules || tenant.modules;
  const hasActivatedModules = Array.isArray(activatedModules)
    ? activatedModules.length > 0
    : Boolean(activatedModules && Object.keys(activatedModules).length > 0);
  const hasCapabilities = asArray(tenantCapabilities).some((row) => row?.tenant_id === academyId);

  return {
    tenantActive: tenant.is_active === true,
    modulesEnabled: hasActivatedModules || hasCapabilities,
    testUsersPresent: asArray(profiles).some((p) => p?.tenant_id === academyId),
    campusPresent: asArray(campuses).some((c) => c?.tenant_id === academyId),
    schoolPresent: asArray(schools).some((s) => s?.tenant_id === academyId),
  };
}

/**
 * Enregistre la preuve d'un contrôle d'accessibilité réel — à appeler
 * UNIQUEMENT après un vrai test runtime (RPC + requête scopée réussies),
 * jamais de façon synthétique. Réutilise `audit_logs` (aucune table dédiée).
 */
export async function recordAcademyAccessibilityCheck(tenant, { ok, route, reason, checkedByUserId } = {}) {
  if (!tenant?.id) return;
  try {
    await supabase.from('audit_logs').insert({
      user_id: checkedByUserId || null,
      action: 'ACADEMY_ACCESSIBILITY_CHECK',
      tenant_id: tenant.id,
      table_name: 'tenants',
      record_id: tenant.id,
      payload: { status: Boolean(ok), route: route || null, reason: reason || null, source: 'runtime_probe' },
    });
  } catch { /* échec de journalisation : silencieux, le contrôle reste affiché à l'écran */ }
}

/** Dernière ligne `audit_logs` correspondant à une action/preuve donnée pour ce tenant. */
async function fetchLatestProof(tenantId, action, metadataMatch) {
  try {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('action', action)
      .order('created_at', { ascending: false })
      .limit(10);
    if (error || !Array.isArray(data)) return null;
    return data.find((row) => metadataMatch(row.payload || {})) || null;
  } catch {
    return null;
  }
}

/**
 * 2 + 3. Collecte les preuves opérationnelles réelles (accessibilité +
 * supervision) déjà journalisées dans `audit_logs` par des actions Master
 * réelles — ne fabrique jamais de preuve, ne les initialise jamais à `true`
 * par défaut.
 */
export async function collectAcademyOperationalEvidence(academyTenantId) {
  if (!academyTenantId) {
    return { academyAccessible: null, masterCanInspect: null, preservesTenantContext: null };
  }

  // `data_verified === true` ne suffit PAS à créditer l'inspection : une
  // politique RLS ne lève pas d'erreur, elle FILTRE — le comptage scopé revient
  // alors 200/error=null/count=0 et le producteur écrit honnêtement
  // data_verified=true avec verified_row_count=0. Un Master qui n'a RIEN lu
  // produirait ainsi une preuve verte. Présence ≠ fonctionnement : on exige un
  // compte STRICTEMENT positif, seul témoin qu'une ligne du tenant a réellement
  // été lue. Aucun faux blocage à craindre pour un tenant réellement prêt : la
  // readiness exige déjà `campusPresent` par ailleurs, donc un compte nul sur
  // `campuses` ne peut signifier que « rien n'a été lu ». Les lignes d'audit
  // antérieures, dépourvues de `verified_row_count`, ne comptent plus — leur
  // provenance est invérifiable, et une inspection rejouée en produit une vraie.
  const inspectionProuvee = (m) =>
    m.new_context === 'INSPECTION_CONTEXT'
    && m.data_verified === true
    && typeof m.verified_row_count === 'number'
    && m.verified_row_count > 0;

  const [accessRow, inspectRow, restoreRow] = await Promise.all([
    fetchLatestProof(academyTenantId, 'ACADEMY_ACCESSIBILITY_CHECK', (m) => m.status === true),
    fetchLatestProof(academyTenantId, 'MASTER_CONTEXT_SWITCH', inspectionProuvee),
    fetchLatestProof(academyTenantId, 'MASTER_CONTEXT_SWITCH', (m) => m.new_context === 'PLATFORM_CONTEXT' && m.context_restored === true && m.cross_tenant_leak_detected === false),
  ]);

  return {
    academyAccessible: accessRow ? {
      status: true, tenantId: academyTenantId, route: accessRow.payload?.route || null,
      checkedAt: accessRow.created_at, source: 'runtime_probe',
    } : null,
    masterCanInspect: inspectRow ? {
      status: true, inspectedTenantId: academyTenantId, masterUserId: inspectRow.user_id || null,
      // Le nombre de lignes réellement lues accompagne la preuve : une preuve
      // sans sa mesure redevient un booléen sans provenance.
      verifiedRowCount: inspectRow.payload?.verified_row_count ?? null,
      checkedAt: inspectRow.created_at, source: 'tenant_inspection',
    } : null,
    preservesTenantContext: restoreRow ? {
      status: true, expectedTenantId: academyTenantId, resolvedTenantId: academyTenantId,
      masterContextPreserved: true, crossTenantLeakDetected: false,
      checkedAt: restoreRow.created_at, source: 'context_integrity_check',
    } : null,
  };
}

/**
 * Collecteur central — point d'entrée unique consommé par
 * `validateAcademyTenantReadiness()`. `operationalEvidence` est injectable
 * (tests, ou résultat déjà chargé par ailleurs) ; par défaut il est collecté
 * via `collectAcademyOperationalEvidence`.
 */
export async function collectAcademyTenantReadiness(tenant, provisioningEvidence = {}, operationalEvidence = null) {
  const checkedAt = new Date().toISOString();
  if (!tenant?.id) {
    return {
      provisioned: null,
      access: { academyAccessible: null },
      supervision: { masterCanInspect: null, preservesTenantContext: null },
      ready: false,
      blockers: ['academy_tenant_not_found'],
      checkedAt,
    };
  }

  const provisioned = deriveAcademyProvisioning(tenant, provisioningEvidence);
  const operational = operationalEvidence || await collectAcademyOperationalEvidence(tenant.id);

  const blockers = [];
  if (!provisioned.tenantActive) blockers.push('tenant_inactive');
  if (!provisioned.modulesEnabled) blockers.push('modules_not_enabled');
  if (!provisioned.testUsersPresent) blockers.push('test_users_missing');
  if (!provisioned.campusPresent) blockers.push('campus_missing');
  if (!provisioned.schoolPresent) blockers.push('school_missing');
  if (!operational.academyAccessible) blockers.push('accessibility_not_verified');
  if (!operational.masterCanInspect) blockers.push('inspection_not_verified');
  if (!operational.preservesTenantContext) blockers.push('context_preservation_not_verified');

  return {
    provisioned,
    access: { academyAccessible: operational.academyAccessible },
    supervision: {
      masterCanInspect: operational.masterCanInspect,
      preservesTenantContext: operational.preservesTenantContext,
    },
    ready: blockers.length === 0,
    blockers,
    checkedAt,
  };
}
